// ── Bank Account Mapping ────────────────────────────────────────────────────
//
// A stream of transactions arrives carrying whatever the bank calls the account
// — "Business Adv Relationship", a mask, sometimes nothing. dbo.BankAccounts is
// where a portfolio manager records what the account actually is. This page is
// where somebody says which is which, once, and every screen downstream stops
// guessing.
//
// THE PAGE SHOWS THE EVIDENCE, NOT A DROPDOWN. The hard cases here are not
// "which of these is obviously it" — the obvious ones were matched
// automatically on the last four and never reach this page. What is left is
// genuinely ambiguous: two records ending in the same digits, one with a full
// account number under one entity, one with only four digits under another. A
// <select> of bank names would hide exactly the fields that decide it. So each
// candidate is a row carrying its account number length, its borrower entity,
// its description and when it was created, and the ones whose last four match
// are marked.
//
// WHY THE ANSWERS ARE SHOWN BACK. A queue-only screen cannot be corrected on. A
// stream tied to the wrong account leaves the queue and its only symptom is a
// bank name that reads plausibly and is wrong. The linked table below is how
// that becomes findable, and unlinking puts the stream back in the queue.
//
// NOTHING HERE TOUCHES A TRANSACTION. Mapping writes one row to
// dbo.BankAccountLink; the transactions are read through a view that resolves
// against it. That is why a wrong answer costs nothing but a correction, and
// why the correction is instant rather than a rewrite of 4,285 rows.
import { useState, useEffect, useCallback } from "react";
import { fmtDate, localFilter } from "../../../shared/format";
import { Btn, Card, ErrorMsg, SearchBar, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { OldPlatformAccounts, IgnoreModal, IGNORE_REASONS } from "../components/OldPlatformAccounts";

const num = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());

const ORIGIN = {
  plaid:      { label: "Plaid",       tone: "#0F6E56", bg: "#E1F5EE" },
  migration:  { label: "Migrated",    tone: "#4A4192", bg: "#ECEAF8" },
  fileimport: { label: "File import", tone: "#8A6100", bg: "#FBF0D9" },
  // A link answered on this page. Without this entry an operator's own answer
  // would render as a red "Unknown" in the mapped table, which is the opposite
  // of the truth — it is the most trustworthy source there is.
  manual:     { label: "Mapped here", tone: "#1F5673", bg: "#E8F4F8" },
  unknown:    { label: "Unknown",     tone: "#993C1D", bg: "#FAECE7" },
};

const Pill = ({ origin }) => {
  const o = ORIGIN[origin] || ORIGIN.unknown;
  return <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 999,
                        fontWeight: 600, background: o.bg, color: o.tone }}>{o.label}</span>;
};

// The stored account number's length, said plainly. A record holding four
// digits is not a shorter account number, it is a record nobody finished — and
// it is the single most useful thing on the screen when two candidates tie.
const digitsNote = (d) => {
  if (d === null || d === undefined) return "no account number on file";
  if (d <= 4) return `only ${d} digit${d === 1 ? "" : "s"} stored`;
  return `${d} digits stored`;
};

const CandidateRow = ({ ba, matches, selected, onSelect, disabled }) => (
  <tr onClick={disabled ? undefined : () => onSelect(ba.id)}
      style={{ cursor: disabled ? "default" : "pointer",
               background: selected ? "#E8F4F8" : "transparent" }}>
    <Td>
      <input type="radio" checked={!!selected} disabled={disabled}
             onChange={() => onSelect(ba.id)} />
    </Td>
    <Td>{ba.bankname || "—"}</Td>
    <Td>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12,
                     fontWeight: matches ? 700 : 400 }}>
        {ba.last4 ? "••••" + ba.last4 : "—"}
      </span>
      {matches && <span style={{ marginLeft: 6, fontSize: 11, color: "#0F6E56" }}>match</span>}
    </Td>
    <Td><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
      {digitsNote(ba.digits)}
    </span></Td>
    <Td>{ba.borrowerguarantorname || "—"}</Td>
    <Td>{ba.description || "—"}</Td>
    <Td>{ba.primaryaccount ? "Primary" : ""}</Td>
    <Td><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
      {ba.createdat || "—"}
    </span></Td>
  </tr>
);

const QueueCard = ({ row, accounts, busy, canEdit, onSave, onIgnored }) => {
  const [pick, setPick] = useState("");
  const [ignoring, setIgnoring] = useState(false);
  const mine = accounts.filter((a) => String(a.accountid) === String(row.accountId));
  const mask = row.reported_mask ? String(row.reported_mask).padStart(4, "0") : null;
  const matches = new Set(mine.filter((a) => a.last4 && mask && a.last4 === mask).map((a) => a.id));

  return (
    <Card style={{ marginBottom: 14, overflowX: "auto" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{row.companyname || row.accountId}</span>
        <Pill origin={row.origin} />
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {num(row.txns)} transactions · {fmtDate(row.first_txn)} to {fmtDate(row.last_txn)}
        </span>
        {canEdit && <span style={{ marginLeft: "auto" }}>
          <Btn onClick={() => setIgnoring(true)}>Ignore</Btn></span>}
      </div>
      {ignoring && <IgnoreModal title={`Ignore ${row.reported_name || "this stream"} — ${row.companyname || ""}`}
                                kind="stream" subjectkey={row.plaidAccountId}
                                onClose={() => setIgnoring(false)} onDone={() => { setIgnoring(false); onIgnored(); }} />}

      <div style={{ margin: "8px 0 12px", fontSize: 13 }}>
        The bank calls it{" "}
        <strong>{row.reported_name || "(no name)"}</strong>
        {mask && <> ending <span style={{ fontFamily: "var(--font-mono)" }}>••••{mask}</span></>}
        {row.reported_bank && <> at <strong>{row.reported_bank}</strong></>}.
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11,
                      color: "var(--color-text-secondary)", marginTop: 3 }}>
          {row.plaidAccountId}
        </div>
      </div>

      {mine.length === 0 ? (
        <div style={{ fontSize: 13, padding: "10px 0", color: "#993C1D" }}>
          This borrower has no bank accounts on file at all. Add the account on the
          borrower's Bank Accounts tab first — there is nothing here to map to.
        </div>
      ) : (
        <>
          {matches.size === 0 && (
            <div style={{ fontSize: 12, marginBottom: 8, color: "#8A6100" }}>
              None of this borrower's accounts end in those digits. Either the account
              was never entered, or one of the records below is missing its number.
            </div>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
            <thead><tr>
              <Th></Th><Th>Bank</Th><Th>Last 4</Th><Th>Number on file</Th>
              <Th>Borrower / Guarantor</Th><Th>Description</Th><Th></Th><Th>Added</Th>
            </tr></thead>
            <tbody>
              {mine.map((ba) => (
                <CandidateRow key={ba.id} ba={ba} matches={matches.has(ba.id)}
                              selected={pick === ba.id} onSelect={setPick}
                              disabled={!canEdit || !!busy} />
              ))}
            </tbody>
          </table>
        </>
      )}

      {canEdit && mine.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <Btn variant="primary" disabled={!pick || !!busy}
               onClick={() => onSave(row, pick)}>
            {busy ? "Saving…" : "Tie these transactions to this account"}
          </Btn>
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            {num(row.txns)} transactions will start showing this bank and number.
          </span>
        </div>
      )}
    </Card>
  );
};

export const BankAccountMappingPage = () => {
  const { api, role, setConfirm } = useApp();

  const [queue, setQueue]   = useState([]);
  const [accts, setAccts]   = useState([]);
  const [linked, setLinked] = useState([]);
  const [ignored, setIgnored] = useState([]);
  const [showIgnored, setShowIgnored] = useState(false);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState(null);
  const [busy, setBusy]     = useState(null);
  const [q, setQ]           = useState("");
  const [showLinked, setShowLinked] = useState(false);

  const load = useCallback(() => {
    setLoad(true); setErr(null);
    api.get("getBankAccountLinkQueue")
      .then((d) => {
        setQueue(Array.isArray(d && d.queue) ? d.queue : []);
        setAccts(Array.isArray(d && d.bankAccounts) ? d.bankAccounts : []);
        setLinked(Array.isArray(d && d.linked) ? d.linked : []);
        setIgnored(Array.isArray(d && d.ignored) ? d.ignored : []);
        setLoad(false);
      })
      .catch((e) => { setErr(e.message); setLoad(false); });
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (row, bankaccountid) => {
    setBusy(row.plaidAccountId); setErr(null);
    try {
      await api.post("saveBankAccountLink",
        { plaidAccountId: row.plaidAccountId, bankaccountid });
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const unlink = (l) => setConfirm({
    msg: `Untie ${num(l.txns)} transactions from ${l.bankname || "this account"}`
       + `${l.last4 ? " ••••" + l.last4 : ""}? They go back to reading "Unmapped account" `
       + `until someone maps them again. No transaction is deleted.`,
    onConfirm: async () => {
      setBusy(l.plaidAccountId); setErr(null);
      try {
        await api.post("saveBankAccountLink",
          { plaidAccountId: l.plaidAccountId, bankaccountid: null });
        load();
      } catch (e) { setErr(e.message); }
      finally { setBusy(null); }
    },
  });

  const shown = localFilter(queue, q,
    ["companyname", "reported_name", "reported_bank", "reported_mask", "plaidAccountId"]);
  const shownLinked = localFilter(linked, q,
    ["companyname", "bankname", "last4", "plaidAccountId"]);
  const affected = queue.reduce((s, r) => s + (Number(r.txns) || 0), 0);

  if (loading) return <Spinner />;
  if (err && !queue.length && !linked.length) return <ErrorMsg msg={err} onRetry={load} />;

  return <>
    <SectionHeader title="Bank Account Mapping" actions={[
      <span key="n" style={{ fontSize: 12, color: "var(--color-text-secondary)", alignSelf: "center" }}>
        {queue.length === 0
          ? "Every stream of transactions is tied to a bank account."
          : `${queue.length} unmapped · ${num(affected)} transactions cannot name their bank`}
      </span>,
      <Btn key="r" onClick={load}>Reload</Btn>,
    ]} />

    <Card style={{ padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <SearchBar value={q} onChange={setQ} placeholder="Company, bank, name, last four…" />
        <Btn onClick={() => setShowLinked((v) => !v)}>
          {showLinked ? "Hide mapped" : `Show mapped (${linked.length})`}
        </Btn>
        {ignored.length > 0 && <Btn onClick={() => setShowIgnored((v) => !v)}>
          {showIgnored ? "Hide ignored" : `Show ignored (${ignored.length})`}
        </Btn>}
      </div>
    </Card>

    {/* Old-platform accounts first: they can need an answer before any of
        their transactions exist here, so the queue below can never show them. */}
    <OldPlatformAccounts canEdit={role.canEdit} q={q} />
    <div style={{ height: 14 }} />

    {err && (queue.length > 0 || linked.length > 0) && (
      <Card style={{ padding: "10px 14px", marginBottom: 14, background: "#FAECE7",
                     borderLeft: "3px solid #993C1D" }}>
        <span style={{ fontSize: 12, color: "#993C1D" }}>{err}</span>
      </Card>
    )}

    {shown.length === 0 && (
      <Card style={{ padding: 40, textAlign: "center", fontSize: 13,
                     color: "var(--color-text-secondary)" }}>
        {queue.length === 0
          ? "Nothing to map. Every stream of transactions is tied to a bank account."
          : "No unmapped streams match that search."}
      </Card>
    )}

    {shown.map((row) => (
      <QueueCard key={row.plaidAccountId} row={row} accounts={accts}
                 busy={busy === row.plaidAccountId} canEdit={role.canEdit}
                 onSave={save} onIgnored={load} />
    ))}

    {showIgnored && (
      <Card style={{ marginTop: 18, overflowX: "auto" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Ignored streams</div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
          Set aside as not needing a mapping. Their transactions are untouched; Restore puts them back in the queue.
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead><tr><Th>Company</Th><Th>Bank calls it</Th><Th>Transactions</Th><Th>Why</Th><Th>By</Th><Th></Th></tr></thead>
          <tbody>
            {localFilter(ignored, q, ["companyname", "reported_name", "reported_bank", "reported_mask", "plaidAccountId", "note"]).map((g) => (
              <tr key={g.plaidAccountId}>
                <Td>{g.companyname || "—"}</Td>
                <Td>{g.reported_name || "(no name)"}{g.reported_mask ? ` ••••${g.reported_mask}` : ""}
                  {g.reported_bank ? <span style={{ color: "var(--color-text-secondary)" }}> · {g.reported_bank}</span> : null}</Td>
                <Td>{num(g.txns)}</Td>
                <Td title={g.note || ""}>{IGNORE_REASONS[g.reason] || g.reason}{g.note ? " *" : ""}</Td>
                <Td><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{g.ignoredby || "—"}</span></Td>
                <Td style={{ textAlign: "right" }}>
                  {role.canEdit && <Btn disabled={busy === g.plaidAccountId} onClick={async () => {
                    setBusy(g.plaidAccountId); setErr(null);
                    try { await api.post("saveBankMappingIgnore", { kind: "stream", key: g.plaidAccountId, restore: true }); load(); }
                    catch (e) { setErr(e.message); }
                    finally { setBusy(null); }
                  }}>Restore</Btn>}
                </Td>
              </tr>))}
          </tbody>
        </table>
      </Card>
    )}

    {showLinked && (
      <Card style={{ marginTop: 18, overflowX: "auto" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Already mapped</div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
          Shown so a wrong answer can be found and corrected. "Last four" matched
          automatically; "operator" was answered here.
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead><tr>
            <Th>Company</Th><Th>Bank</Th><Th>Last 4</Th><Th>Transactions</Th>
            <Th>Origin</Th><Th>Matched by</Th><Th>By</Th><Th></Th>
          </tr></thead>
          <tbody>
            {shownLinked.map((l) => (
              <tr key={l.plaidAccountId}>
                <Td>{l.companyname || "—"}</Td>
                <Td>{l.bankname || <span style={{ color: "#993C1D" }}>missing bank account</span>}</Td>
                <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                  {l.last4 ? "••••" + l.last4 : "—"}</span></Td>
                <Td>{num(l.txns)}</Td>
                <Td><Pill origin={l.source} /></Td>
                <Td><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                  {l.matchedby || "—"}</span></Td>
                <Td><span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                  {l.createdby || "—"}</span></Td>
                <Td style={{ textAlign: "right" }}>
                  {role.canEdit && (
                    <Btn disabled={busy === l.plaidAccountId} onClick={() => unlink(l)}>
                      {busy === l.plaidAccountId ? "…" : "Untie"}
                    </Btn>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    )}
  </>;
};
